import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Fade,
  Alert,
  Autocomplete,
  TextField,
  Button,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { useFsCreateCtx } from '@/pages/components/audit/finance-statement/create-stepper/context';
import { useCompanyAnnualForm } from '@/pages/components/audit/finance-statement/create-stepper/hooks/useCompanyAnnualForm';
import { useFormValidation } from '@/pages/components/audit/finance-statement/create-stepper/hooks/useFormValidation';
import { FormSkeleton } from '@/pages/components/audit/finance-statement/create-stepper/components/FormSkeleton';
import * as CompanyAnnualService from '@/services/accountService/companyAnnualService';
import type { CompanyAnnualSelectItem, CompanyAnnual } from '@/services/accountService/companyAnnualService';
import EpPeriod from '@/components/company-info/company-annual/sub-content/EpPeriod';
import EpBankConfirmations from '@/components/company-info/company-annual/sub-content/EpBankConfirmations';
import EpCompanyDetails from '@/components/company-info/company-annual/sub-content/EpCompanyDetails';
import EpAuditor from '@/components/company-info/company-annual/sub-content/EpAuditor';
import FirstAuditPeriodConfirmDialog from '@/components/company-info/company-annual/FirstAuditPeriodConfirmDialog';
import { useFirstAuditPeriodConfirm } from '@/components/company-info/company-annual/useFirstAuditPeriodConfirm';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import { computeYearOfAssessment } from '@/utils/dataUtils';
import { normalizeContentSheetDates } from '@/pages/components/audit/finance-statement/create-stepper/normalizeContentDates';

export default function StepBCaBasicInfo() {
  const { state, dispatch } = useFsCreateCtx();
  const setAnnualCache = useCompanyAnnualStore(s => s.setAnnual);
  const [showContent, setShowContent] = useState(false);

  // Annual selection states
  const [annuals, setAnnuals] = useState<CompanyAnnualSelectItem[]>([]);
  const [annualLoading, setAnnualLoading] = useState(false);
  const annualsLoadedRef = useRef(false);
  const [annualError, setAnnualError] = useState<string | null>(null);
  const [annualInput, setAnnualInput] = useState('');
  const annualIdleRef = useRef<any>(null);
  const [showCreateAnnual, setShowCreateAnnual] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [restoredData, setRestoredData] = useState<any>(() => {
    if (!state.isCreatingAnnual) return null;
    return state.draftData?.formData || null;
  });
  const [isRestoring, setIsRestoring] = useState(false);
  
  // Field errors state for validation
  const [fieldErrors, setFieldErrors] = useState<{
    periodFrom?: boolean;
    periodTo?: boolean;
    lastYearPeriodFrom?: boolean;
    lastYearPeriodTo?: boolean;
    auditCertificateId?: boolean;
  }>({});

  const selectedCompany = state.selectedCompany;
  const selectedAnnual = state.selectedAnnual as any;
  const hasImportedContent = !!state.contentData;
  const companyId = (selectedCompany as any)?.companyId ?? (selectedCompany as any)?.id;
  const companyName = (selectedCompany as any)?.companyName ?? (selectedCompany as any)?.name;
  const annualId = state.annualId || (selectedAnnual?.annualId ?? selectedAnnual?.id);

  // Auto-detect first audit: periodFrom === inCorpDate
  const companyInCorpDate = (selectedCompany as any)?.inCorpDate || null;

  // 使用 useRef 保存最新值，避免依赖项问题
  const formRef = useRef<CompanyAnnual | null>(null);
  const isCreatingAnnualRef = useRef(state.isCreatingAnnual);
  const annualIdRef = useRef(state.annualId);
  const restoredFromDraftRef = useRef(false);

  // Toggle create visibility with selection and creating mode
  useEffect(() => {
    setShowCreateAnnual(!selectedAnnual && !state.isCreatingAnnual);
  }, [selectedAnnual, state.isCreatingAnnual]);

  // Load annuals when company changes
  useEffect(() => {
    if (!companyId) {
      setAnnuals([]);
      dispatch({ type: 'set_annual', payload: null as any });
      return;
    }
    const run = async () => {
      setAnnualLoading(true);
      setAnnualError(null);
      try {
        const list = await CompanyAnnualService.querySelectionList(Number(companyId));
        setAnnuals(list || []);
        annualsLoadedRef.current = true;
      } catch (e: any) {
        setAnnualError(e?.message || 'Failed to load annuals');
      } finally {
        setAnnualLoading(false);
      }
    };
    run();
  }, [companyId, dispatch]);

  const { validationErrors, validateRequiredFields, clearValidationErrors } = useFormValidation();

  // 使用 useCallback 稳定回调函数引用
  const handleLoadComplete = useCallback(() => {
    setTimeout(() => setShowContent(true), 100);
  }, []);

  const handleSaveComplete = useCallback((data: CompanyAnnual | null) => {
    if (data && data.annualId && data.annualId > 0 && data.annualId !== annualIdRef.current) {
      dispatch({ type: 'set_annual_id', payload: data.annualId });
    }
  }, [dispatch]);

  const { form, loading, error, updateForm, forceSave, saveData } = useCompanyAnnualForm({
    annualId: annualId && Number(annualId) > 0 ? Number(annualId) : undefined,
    companyId: isRestoring ? undefined : (companyId ? Number(companyId) : undefined), // 恢复时不传 companyId
    autoSave: false, // 暂时关闭自动保存
    initialData: restoredData,
    onLoadComplete: handleLoadComplete,
    onSaveComplete: handleSaveComplete,
  });

  // ========== Content Sheet Import: unified match + patch ==========
  // Build the content patch from contentData (reusable helper)
  const buildContentPatch = useCallback((cd: any): Partial<CompanyAnnual> => {
    const { data: dates } = normalizeContentSheetDates(cd as any);
    const patch: Partial<CompanyAnnual> = {};
    // EpPeriod fields — dates already normalized to dd/MM/yyyy
    if (dates.periodFrom) patch.periodFrom = dates.periodFrom;
    if (dates.periodTo) patch.periodTo = dates.periodTo;
    if (dates.lastYearPeriodFrom) patch.lastYearPeriodFrom = dates.lastYearPeriodFrom;
    if (dates.lastYearPeriodTo) patch.lastYearPeriodTo = dates.lastYearPeriodTo;
    if (dates.changeOfFinancialYearEnd != null) patch.changeOfFinancialYearEnd = dates.changeOfFinancialYearEnd;
    if (dates.dateOfBoardResolution) patch.dateOfBoardResolution = dates.dateOfBoardResolution;
    if (dates.reasonToChange) patch.reasonToChange = dates.reasonToChange;
    if (dates.cessationDuringThisPeriod != null) patch.cessationDuringThisPeriod = dates.cessationDuringThisPeriod;
    if (dates.multiYearPl) patch.isMultiYearAudit = true;
    // EpCompanyDetails fields
    if (dates.limitByGuarantee != null) patch.limitByGuarantee = dates.limitByGuarantee;
    if (dates.liabilitiesAmount != null) patch.liabilitiesAmount = dates.liabilitiesAmount;
    if (dates.liabilitiesCurrency) patch.liabilitiesCurrency = dates.liabilitiesCurrency;
    if (dates.otherNameThanCompany) patch.otherNameThanCompany = dates.otherNameThanCompany;
    if (dates.section88 != null) patch.section88 = dates.section88;
    if (dates.taxFileNo) patch.taxFileNo = dates.taxFileNo;
    // EpAuditor fields
    if (dates.auditCertificateId) patch.auditCertificateId = dates.auditCertificateId;
    return patch;
  }, []);

  // Phase 1: match annual by yearOfAssessment (robust, format-agnostic) + Phase 2: overlay Excel data
  const contentMatchedRef = useRef(false);
  const contentPatchedRef = useRef(false);
  useEffect(() => {
    if (contentMatchedRef.current) return;
    const cd = state.contentData;
    if (!cd || !cd.periodFrom || !cd.periodTo) return;
    if (!companyId || annualLoading || !annualsLoadedRef.current) return; // wait until annuals are actually loaded

    contentMatchedRef.current = true;

    // Match by yearOfAssessment (computed from periodTo) — robust against date format differences
    const excelYoa = computeYearOfAssessment(cd.periodTo);
    const matched = excelYoa
      ? annuals.find((a) => a.yearOfAssessment === excelYoa)
      : null;

    // Build patch from Excel content data
    const patch = buildContentPatch(cd);

    if (matched) {
      // Selection mode: select the matched annual
      dispatch({ type: 'set_annual', payload: matched as any });
      dispatch({ type: 'set_annual_id', payload: matched.annualId });
      dispatch({ type: 'set_is_creating_annual', payload: false });

      // Pre-populate store cache with Excel overlay so Ep* children find data on mount
      if (Object.keys(patch).length > 0) {
        setAnnualCache({ annualId: matched.annualId, companyId: Number(companyId), ...patch } as CompanyAnnual);
      }
    } else {
      // Creation mode: no existing annual matches
      dispatch({ type: 'set_is_creating_annual', payload: true });
      const tempAnnualId = -Date.now();
      dispatch({ type: 'set_annual_id', payload: tempAnnualId });

      // Pre-populate store cache so Ep* children find data on mount
      if (Object.keys(patch).length > 0) {
        setAnnualCache({ annualId: tempAnnualId, companyId: Number(companyId), ...patch } as CompanyAnnual);
      }
    }
  }, [state.contentData, annuals, annualLoading, companyId, dispatch, setAnnualCache, buildContentPatch]);

  // Phase 2: overlay Excel data onto the hook's form state once it loads
  useEffect(() => {
    if (contentPatchedRef.current) return;
    if (!contentMatchedRef.current) return; // Phase 1 must complete first
    const cd = state.contentData;
    if (!cd || !form) return;

    contentPatchedRef.current = true;
    const patch = buildContentPatch(cd);

    if (Object.keys(patch).length > 0) {
      updateForm(patch);

      // Also refresh store cache with full form + patch data
      const currentAnnualId = annualId;
      if (currentAnnualId) {
        setAnnualCache({ ...form, ...patch, annualId: currentAnnualId } as CompanyAnnual);
      }
    }
  }, [state.contentData, form, updateForm, annualId, setAnnualCache, buildContentPatch]);

  // Auto-detect first audit: periodFrom === inCorpDate (after form is available)
  const isFirstAuditForced = useMemo(() => {
    if (!companyInCorpDate || !form?.periodFrom) return false;
    return companyInCorpDate === form.periodFrom;
  }, [companyInCorpDate, form?.periodFrom]);

  // Auto-set firstAudit = 1 when periodFrom === inCorpDate
  useEffect(() => {
    if (
      isFirstAuditForced
      && form
      && (
        (form as any).firstAudit !== 1
        || (form as any).lastYearPeriodFrom
        || (form as any).lastYearPeriodTo
      )
    ) {
      updateForm({
        firstAudit: 1,
        lastYearPeriodFrom: null,
        lastYearPeriodTo: null,
      } as any);
    }
  }, [isFirstAuditForced, form?.firstAudit, form?.lastYearPeriodFrom, form?.lastYearPeriodTo, updateForm]);

  useEffect(() => {
    restoredFromDraftRef.current = false;
  }, [state.draftData]);

  useEffect(() => {
    if (state.isCreatingAnnual) {
      return;
    }
    restoredFromDraftRef.current = false;
    setRestoredData(null);
    setIsRestoring(false);
  }, [state.isCreatingAnnual]);

  useEffect(() => {
    if (restoredFromDraftRef.current) {
      return;
    }
    if (!state.draftData?.formData) {
      return;
    }
    const currentCompanyId = (state.selectedCompany as any)?.companyId ?? (state.selectedCompany as any)?.id;
    if (Number(state.draftData.companyId || 0) !== Number(currentCompanyId || 0)) {
      return;
    }

    const expectedAnnualId = state.annualId ?? ((state.selectedAnnual as any)?.annualId ?? (state.selectedAnnual as any)?.id ?? null);
    const draftAnnualId = Number((state.draftData as any)?.annualId ?? 0) || null;
    if (!state.isCreatingAnnual) {
      if (!draftAnnualId || draftAnnualId <= 0) {
        return;
      }
      if (expectedAnnualId && Number(expectedAnnualId) > 0 && Number(expectedAnnualId) !== Number(draftAnnualId)) {
        return;
      }
    }

    restoredFromDraftRef.current = true;
    setIsRestoring(true);

    let effectiveAnnualId = draftAnnualId || expectedAnnualId || null;
    if (!effectiveAnnualId && state.isCreatingAnnual) {
      effectiveAnnualId = -Date.now();
    }
    if (effectiveAnnualId !== state.annualId) {
      dispatch({ type: 'set_annual_id', payload: effectiveAnnualId });
    }

    const dataWithTempId = { ...(state.draftData.formData as any), annualId: effectiveAnnualId };
    setAnnualCache(dataWithTempId);
    setRestoredData(dataWithTempId);

    setTimeout(() => {
      setIsRestoring(false);
    }, 100);
  }, [dispatch, setAnnualCache, state.draftData, state.annualId, state.isCreatingAnnual, state.selectedCompany]);

  // 更新 ref 的值
  useEffect(() => {
    formRef.current = form;
    isCreatingAnnualRef.current = state.isCreatingAnnual;
    annualIdRef.current = state.annualId;
  }, [form, state.isCreatingAnnual, state.annualId]);

  const applyFirstAuditConfirmPatch = useCallback((patch: {
    firstAuditFollows18Months?: boolean | null;
    firstAuditYearEndMD?: string | null;
  }) => {
    updateForm(patch);
    const id = Number(annualIdRef.current || 0);
    if (id > 0) {
      const existing = useCompanyAnnualStore.getState().byAnnualId[id];
      if (existing) {
        setAnnualCache({...existing, ...patch} as CompanyAnnual);
      }
    }
  }, [updateForm, setAnnualCache]);

  const {
    dialogOpen: firstAuditDialogOpen,
    handleConfirm: handleFirstAuditConfirm,
    isBlocking: isFirstAuditBlocking,
    blockingMessage: firstAuditBlockingMessage,
  } = useFirstAuditPeriodConfirm({
    periodFrom: form?.periodFrom,
    periodTo: form?.periodTo,
    inCorpDate: companyInCorpDate,
    cessationDuringThisPeriod: form?.cessationDuringThisPeriod,
    firstAuditYearEndMD: form?.firstAuditYearEndMD,
    onPatch: applyFirstAuditConfirmPatch,
  });

  const [firstAuditBlockAlert, setFirstAuditBlockAlert] = useState<string | null>(null);
  const periodConfirmValidateRef = useRef<(() => boolean) | null>(null);

  // 使用 useCallback 包装 beforeNext 函数，参考 StepCCaAuditConfig
  const handleBeforeNext = useCallback(async (clearDraft = true) => {
    try {
      dispatch({ type: 'set_is_saving', payload: true });
      
      // 确保 annualId 存在于 Context 中（选择已有 Annual 的情况）
      if (!state.isCreatingAnnual && selectedAnnual) {
        const extractedId = (selectedAnnual as any)?.annualId ?? (selectedAnnual as any)?.id;
        if (extractedId && extractedId > 0 && extractedId !== state.annualId) {
          dispatch({ type: 'set_annual_id', payload: extractedId });
        }
      }
      
      // ========== 第一步：先校验数据 ==========
      if (isCreatingAnnualRef.current) {
        const errors: typeof fieldErrors = {};
        // 只有当 periodFrom 和 periodTo 都为空时才设置错误
        if (!formRef.current?.periodFrom && !formRef.current?.periodTo) {
          errors.periodFrom = true;
          errors.periodTo = true;
        }
        
        // 检查 inCorpDate 和 periodFrom 是否相等
        const companyInCorpDate = (state.selectedCompany as any)?.inCorpDate || null;
        const shouldSkipLastYearValidation = companyInCorpDate && formRef.current?.periodFrom && companyInCorpDate === formRef.current.periodFrom;
        
        // 只有当 lastYearPeriodFrom 和 lastYearPeriodTo 都为空时才设置错误
        // 但如果 inCorpDate 和 periodFrom 相等，则跳过此校验
        if (!shouldSkipLastYearValidation) {
          if (!formRef.current?.lastYearPeriodFrom && !formRef.current?.lastYearPeriodTo) {
            errors.lastYearPeriodFrom = true;
            errors.lastYearPeriodTo = true;
          }
        }
        
        if (!formRef.current?.auditCertificateId || formRef.current?.auditCertificateId === 0) {
          errors.auditCertificateId = true;
        }
        
        // 如果有校验错误，立即返回，不进行保存
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          dispatch({ type: 'set_is_saving', payload: false });
          return false;
        }
      }

      // Qualified-opinion period confirm (first >18m / non-first >12m)
      if (periodConfirmValidateRef.current && !periodConfirmValidateRef.current()) {
        dispatch({ type: 'set_is_saving', payload: false });
        return false;
      }

      // Path A: block next until first-audit period confirm is done
      if (isFirstAuditBlocking) {
        setFirstAuditBlockAlert(firstAuditBlockingMessage);
        dispatch({ type: 'set_is_saving', payload: false });
        return false;
      }
      setFirstAuditBlockAlert(null);

      // ========== 第二步：暂存数据，等待 StepCCaAuditConfig 完成后再保存 ==========
      if (isCreatingAnnualRef.current && formRef.current) {
        const currentCompanyId = (state.selectedCompany as any)?.companyId ?? (state.selectedCompany as any)?.id;
        const { annualId: _draftAnnualId, ...draftFormData } = (formRef.current as any) || {};
        dispatch({
          type: 'save_draft',
          payload: {
            companyId: Number(currentCompanyId || 0),
            annualId: null,
            formData: draftFormData,
          },
        });
      }
      
      // 清除字段错误状态
      setFieldErrors({});
      
      // 只在成功前进时清除暂存数据（不在 Back 时清除）
      if (clearDraft) {
        // NOTE: draft data will be cleared after StepCCaAuditConfig saves successfully
      }
      
      dispatch({ type: 'set_is_saving', payload: false });
      return true;
    } catch (e) {
      console.error('Failed to save before next:', e);
      dispatch({ type: 'set_is_saving', payload: false });
      return false;
    }
  }, [dispatch, state.annualId, selectedAnnual, state.selectedCompany, isFirstAuditBlocking, firstAuditBlockingMessage]);

  useEffect(() => {
    dispatch({
      type: 'set_before_next',
      payload: handleBeforeNext,
    });

    return () => {
      dispatch({ type: 'set_before_next', payload: undefined });
    };
  }, [dispatch, handleBeforeNext]);

  // 注册 getFormData 函数
  useEffect(() => {
    const getFormData = () => {
      return formRef.current;
    };

    dispatch({
      type: 'set_get_form_data',
      payload: getFormData,
    });

    return () => {
      dispatch({ type: 'set_get_form_data', payload: undefined });
    };
  }, [dispatch]);

  const handleStartCreating = () => {
    const currentCompanyId = (selectedCompany as any)?.companyId ?? (selectedCompany as any)?.id;
    
    // 检查是否有暂存数据且 companyId 一致
    if (state.draftData && state.draftData.companyId === currentCompanyId && state.draftData.formData) {
      // 恢复之前的数据
      setIsRestoring(true);
      setRestoredData(state.draftData.formData);
      dispatch({ type: 'set_is_creating_annual', payload: true });
      
      // 设置一个临时的 annualId，让 Ep 组件能够工作
      // 使用负数表示这是临时的，还没有保存到后端
      const tempAnnualId = -Date.now();
      const dataWithTempId = { ...state.draftData.formData, annualId: tempAnnualId };
      
      // 将数据设置到 store 缓存中，让 Ep 组件能够读取
      setAnnualCache(dataWithTempId);
      setRestoredData(dataWithTempId);
      dispatch({ type: 'set_annual_id', payload: tempAnnualId });
      
      // 恢复完成后重置状态
      setTimeout(() => {
        setIsRestoring(false);
      }, 100);
    } else {
      // 不同的 company 或没有暂存数据，初始化新数据
      setIsRestoring(false);
      setRestoredData(null);
      dispatch({ type: 'clear_draft' });
      dispatch({ type: 'set_is_creating_annual', payload: true });
      dispatch({ type: 'set_annual_id', payload: null });
    }
    clearValidationErrors();
  };

  const handleCancelCreating = async () => {
    // 暂时注释掉数据检查，直接取消
    // if (form && (form.periodFrom || form.periodTo || form.taxFileNo)) {
    //   setCancelDialogOpen(true);
    // } else {
    //   cancelCreatingMode();
    // }
    cancelCreatingMode();
  };

  const cancelCreatingMode = () => {
    // 退出创建模式
    dispatch({ type: 'set_is_creating_annual', payload: false });
    // 清除 annualId
    dispatch({ type: 'set_annual_id', payload: null });
    // 清除验证错误
    clearValidationErrors();
  };

  // 使用 useCallback 包装 onChange 回调，避免在渲染期间更新状态
  const handlePeriodChange = useCallback((patch: any) => {
    updateForm(patch);
    // 清除相关字段的错误状态
    if (patch.periodFrom !== undefined) {
      setFieldErrors(prev => ({ ...prev, periodFrom: false }));
    }
    if (patch.periodTo !== undefined) {
      setFieldErrors(prev => ({ ...prev, periodTo: false }));
    }
    if (patch.lastYearPeriodFrom !== undefined) {
      setFieldErrors(prev => ({ ...prev, lastYearPeriodFrom: false }));
    }
    if (patch.lastYearPeriodTo !== undefined) {
      setFieldErrors(prev => ({ ...prev, lastYearPeriodTo: false }));
    }
  }, [updateForm]);

  const handleCompanyDetailsChange = useCallback((patch: any) => {
    updateForm(patch);
  }, [updateForm]);

  const handleBankConfirmationsChange = useCallback((patch: any) => {
    updateForm(patch);
  }, [updateForm]);

  const handleAuditorChange = useCallback((patch: any) => {
    updateForm(patch);
    // 清除相关字段的错误状态
    if (patch.auditCertificateId !== undefined) {
      setFieldErrors(prev => ({ ...prev, auditCertificateId: false }));
    }
  }, [updateForm]);

  const confirmCancel = () => {
    setCancelDialogOpen(false);
    cancelCreatingMode();
  };

  if (!selectedCompany) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Please select a company first.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1 }}>
      {firstAuditBlockAlert && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFirstAuditBlockAlert(null)}>
          {firstAuditBlockAlert}
        </Alert>
      )}
      <FirstAuditPeriodConfirmDialog
        open={firstAuditDialogOpen}
        onConfirm={handleFirstAuditConfirm}
      />
      {/* Annual Selection Section - 上传导入后隐藏；非创建模式显示 */}
      {!state.isCreatingAnnual && !hasImportedContent && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Select Annual
            </Typography>
            {annualError && <Alert severity="error" sx={{ mb: 1 }}>{annualError}</Alert>}
            <Autocomplete
              value={selectedAnnual}
              onChange={(_, v) => {
                dispatch({ type: 'set_annual', payload: v as any });
                // 同时设置 annualId，确保一次点击 Next 就能前进
                const selectedAnnualId = (v as any)?.annualId ?? (v as any)?.id ?? null;
                dispatch({ type: 'set_annual_id', payload: selectedAnnualId });
              }}
              options={annuals}
              loading={annualLoading}
              getOptionLabel={(o) => (o as any)?.yearOfAssessment || ''}
              getOptionKey={(o) => {
                const key = (o as any)?.annualId ?? (o as any)?.id;
                return key != null ? String(key) : Math.random().toString();
              }}
              isOptionEqualToValue={(opt, val) => {
                const ok = (opt as any) || {};
                const vk = (val as any) || {};
                return (ok.annualId ?? ok.id) === (vk.annualId ?? vk.id);
              }}
              inputValue={annualInput}
              onInputChange={(_, v) => {
                setAnnualInput(v);
                if (!v || v.trim() === '') {
                  setShowCreateAnnual(!selectedAnnual && !state.isCreatingAnnual);
                  if (annualIdleRef.current) clearTimeout(annualIdleRef.current);
                  return;
                }
                setShowCreateAnnual(false);
                if (annualIdleRef.current) clearTimeout(annualIdleRef.current);
                annualIdleRef.current = setTimeout(() => {
                  setShowCreateAnnual(!selectedAnnual && !state.isCreatingAnnual);
                }, 1200);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && showCreateAnnual) {
                  e.preventDefault();
                  handleStartCreating();
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Annual"
                  placeholder="Type to search or create new annual"
                />
              )}
            />
            {showCreateAnnual && (
              <Button
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={handleStartCreating}
              >
                Create New Annual
              </Button>
            )}
          </Box>

          {!selectedAnnual && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Please select or create an annual to continue.
            </Alert>
          )}
        </>
      )}

      {/* Creating Mode Header - 只在创建模式显示 */}
      {state.isCreatingAnnual && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              Creating New Annual - Step 1/2
            </Typography>
            <Chip label="Draft" color="warning" size="small" />
            <Box flex={1} />
            <Button
              size="small"
              variant="outlined"
              onClick={handleCancelCreating}
            >
              Cancel & Select Existing
            </Button>
          </Stack>
          <Alert severity="info" sx={{ mt: 1 }}>
            Fill in the basic information below. Your data will be automatically saved.
          </Alert>
        </Box>
      )}

      {/* Company Annual Details Section - 只在选择了 Annual 或创建模式下显示 */}
      {((selectedAnnual || annualId) && !state.isCreatingAnnual) && (
        <>
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              Basic Information
            </Typography>
            {state.isSaving && (
              <Chip label="Saving..." color="primary" size="small" variant="outlined" />
            )}
          </Stack>
          {loading ? (
            <FormSkeleton rows={3} showTitle />
          ) : (
            <Fade in={showContent} timeout={300}>
              <Box>
                <EpPeriod
                  annualId={Number(annualId || 0)}
                  onChange={handlePeriodChange}
                  disableAccordion={true}
                  confirmValidateRef={periodConfirmValidateRef}
                  externalErrors={{
                    periodFrom: fieldErrors.periodFrom,
                    periodTo: fieldErrors.periodTo,
                    lastYearPeriodFrom: fieldErrors.lastYearPeriodFrom,
                    lastYearPeriodTo: fieldErrors.lastYearPeriodTo,
                  }}
                />
                <EpBankConfirmations
                  annualId={Number(annualId || 0)}
                  companyName={companyName}
                  periodTo={form?.periodTo || null}
                  auditCertificateId={form?.auditCertificateId || undefined}
                  onChange={handleBankConfirmationsChange}
                  disableAccordion={true}
                  enableRowDownload={true}
                />
                <EpCompanyDetails
                  annualId={Number(annualId || 0)}
                  companyId={companyId}
                  onChange={handleCompanyDetailsChange}
                  disableAccordion={true}
                  hideOverseasProfitsClaims={true}
                />
                <EpAuditor
                  annualId={Number(annualId || 0)}
                  onChange={handleAuditorChange}
                  disableAccordion={true}
                  externalErrors={{
                    auditCertificateId: fieldErrors.auditCertificateId,
                  }}
                />
              </Box>
            </Fade>
          )}
        </>
      )}

      {/* Creating Mode - Basic Information Section */}
      {state.isCreatingAnnual && (
        <>
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              Basic Information
            </Typography>
            {state.isSaving && (
              <Chip label="Saving..." color="primary" size="small" variant="outlined" />
            )}
          </Stack>
          {loading ? (
            <FormSkeleton rows={3} showTitle />
          ) : (
            <Fade in={showContent} timeout={300}>
              <Box>
                <EpPeriod
                  annualId={Number(annualId)}
                  onChange={handlePeriodChange}
                  disableAccordion={true}
                  confirmValidateRef={periodConfirmValidateRef}
                  externalErrors={{
                    periodFrom: fieldErrors.periodFrom,
                    periodTo: fieldErrors.periodTo,
                    lastYearPeriodFrom: fieldErrors.lastYearPeriodFrom,
                    lastYearPeriodTo: fieldErrors.lastYearPeriodTo,
                  }}
                />
                <EpBankConfirmations
                  annualId={Number(annualId)}
                  companyName={companyName}
                  periodFrom={form?.periodFrom || null}
                  auditCertificateId={form?.auditCertificateId || undefined}
                  onChange={handleBankConfirmationsChange}
                  disableAccordion={true}
                  enableRowDownload={true}
                />
                <EpCompanyDetails
                  annualId={Number(annualId)}
                  companyId={companyId}
                  onChange={handleCompanyDetailsChange}
                  disableAccordion={true}
                  hideOverseasProfitsClaims={true}
                />
                <EpAuditor
                  annualId={Number(annualId)}
                  onChange={handleAuditorChange}
                  disableAccordion={true}
                  externalErrors={{
                    auditCertificateId: fieldErrors.auditCertificateId,
                  }}
                />
              </Box>
            </Fade>
          )}
        </>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Creating Annual?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your entered data has been automatically saved and can be recovered later.
          </Alert>
          <Typography>
            Are you sure you want to cancel creating this annual and return to selection?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Continue Editing</Button>
          <Button onClick={confirmCancel} color="error" variant="contained">
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
